import { format } from "date-fns";
import { RefObject } from "react";
import { Chat, User } from "snudbud-backbone-sdk";
import AudioPlayer from "./AudioPlayer";

interface MessageListProps {
  messagesPagination: {
    data: Chat.Message[];
    isLoading: boolean;
    count: number;
  };
  isLoadingOlderMessages: boolean;
  userId?: string;
  user?: User.FetchReturn;
  profile?: User.FetchProfileReturn;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
}

export default function MessageList({
  messagesPagination,
  isLoadingOlderMessages,
  userId,
  user,
  profile,
  messagesEndRef,
  listRef,
}: MessageListProps) {
  // Format message timestamp
  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const messageDate = new Date(date);

    // If message is from today, show only time
    if (messageDate.toDateString() === now.toDateString()) {
      return format(messageDate, "h:mm a");
    }

    // Otherwise show date and time
    return format(messageDate, "MMM d, h:mm a");
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups: Record<string, Chat.Message[]> = {};

    // Sort messages by date (oldest first)
    const sortedMessages = [...messagesPagination.data].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    sortedMessages.forEach((message) => {
      const dateKey = new Date(message.createdAt).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(message);
    });

    return Object.entries(groups).sort(([dateA], [dateB]) => {
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  };

  // Display date separator
  const renderDateSeparator = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let formattedDate = format(date, "MMMM d, yyyy");

    if (date.toDateString() === today.toDateString()) {
      formattedDate = "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      formattedDate = "Yesterday";
    }

    return (
      <div className="flex items-center justify-center my-3">
        <div className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{formattedDate}</div>
      </div>
    );
  };

  return (
    <div ref={listRef} className="flex-1 p-4 overflow-y-auto">
      {messagesPagination.isLoading && messagesPagination.data.length === 0 ? (
        <div className="flex justify-center items-center h-full">
          <div className="animate-pulse flex flex-col items-center">
            <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
            <p className="text-gray-500">Loading messages...</p>
          </div>
        </div>
      ) : messagesPagination.data.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full">
          <svg className="w-16 h-16 mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            ></path>
          </svg>
          <h3 className="mb-2 text-lg font-medium text-gray-800">No messages yet</h3>
          <p className="text-gray-500 mb-6 text-center max-w-md">
            Send a message to start the conversation with {profile?.name || "this user"}.
          </p>
        </div>
      ) : (
        <>
          {messagesPagination.isLoading && isLoadingOlderMessages && (
            <div className="flex justify-center py-2">
              <div className="inline-flex items-center text-gray-500">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Loading earlier messages...
              </div>
            </div>
          )}

          {groupMessagesByDate().map(([dateStr, messages]) => (
            <div key={dateStr}>
              {renderDateSeparator(dateStr)}

              {messages.map((message) => {
                const isFromUser = message.fromUserId === user?.userId;
                const isVoiceMessage = message.audioUrl !== undefined && message.audioUrl !== null;

                return (
                  <div
                    key={message.chatMessageId}
                    className={`flex ${isFromUser ? "justify-end" : "justify-start"} mb-3`}
                  >
                    {!isFromUser && profile && (
                      <img
                        src={profile.profileImageUrl || "https://via.placeholder.com/32"}
                        alt={profile.name}
                        className="w-8 h-8 rounded-full object-cover mr-2 self-end"
                      />
                    )}

                    <div className="flex flex-col max-w-[75%]">
                      <div
                        className={`rounded-2xl px-4 py-3 ${
                          isFromUser
                            ? "bg-blue-600 text-white rounded-br-none"
                            : "bg-gray-100 text-gray-800 rounded-bl-none"
                        } ${isVoiceMessage ? "min-w-[220px]" : ""}`}
                      >
                        {isVoiceMessage ? (
                          <div className="relative">
                            <AudioPlayer
                              audioUrl={message.audioUrl!}
                              isUserMessage={isFromUser}
                              messageId={message.chatMessageId}
                            />
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap break-words">{message.content}</p>
                        )}
                      </div>

                      <span className={`text-xs text-gray-500 mt-1 ${isFromUser ? "text-right" : "text-left"}`}>
                        {formatMessageTime(message.createdAt)}
                        {isFromUser && (
                          <>
                            {message.readAt && <span className="ml-2 text-blue-500">Read</span>}
                            {message.deliveredAt && !message.readAt && (
                              <span className="ml-2 text-gray-400">Delivered</span>
                            )}
                          </>
                        )}
                      </span>
                    </div>

                    {isFromUser && <div className="w-8 h-8 ml-2"></div> /* Spacer for alignment */}
                  </div>
                );
              })}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
}
